/**
 * AdminDashboard - Panel de control principal GOATGYM
 * Métricas de negocio, check-ins pendientes, gráficas, acciones rápidas
 * Mobile-first, carga rápida
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import StatsCard from "../../components/admin/StatsCard";
import ClientCard from "../../components/admin/ClientCard";
import { 
  Users, 
  Crown,
  UserX,
  TrendingUp,
  ClipboardCheck,
  Clock,
  AlertCircle,
  ChevronRight,
  RefreshCw,
  Dumbbell,
  ChefHat,
  Apple,
  Calendar,
  Activity,
  BarChart3,
  Library
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Chart colors
const COLORS = {
  primary: "#D4AF37",
  secondary: "#4A90A4",
  green: "#22C55E",
  orange: "#F97316",
  purple: "#A855F7",
  gray: "#666666"
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-2 shadow-lg">
        <p className="text-white text-xs font-medium">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-xs" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const AdminDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [pendingClients, setPendingClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      
      const [overviewRes, clientsRes, weeklyRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats/overview`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/clients?status=pending&limit=5`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/stats/weekly?weeks=8`, { headers: getAuthHeaders() }).catch(() => ({ data: null }))
      ]);
      
      setStats(overviewRes.data);
      setPendingClients(clientsRes.data.clients || []);
      setWeeklyData(weeklyRes.data);
      
      if (showToast) toast.success("Datos actualizados");
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Error cargando datos");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => fetchData(true);

  const goToClients = (filter) => {
    navigate(`/admin/clients${filter ? `?filter=${filter}` : ""}`);
  };

  const goToPremiumClient = (client) => {
    navigate(`/admin/premium-clients?client=${client.id}`);
  };

  // Prepare chart data
  const userChartData = weeklyData?.users_by_week || [];
  const checkinChartData = weeklyData?.checkins_by_week || [];
  const pieData = weeklyData?.totals ? [
    { name: "Premium", value: weeklyData.totals.premium, color: COLORS.primary },
    { name: "Free", value: weeklyData.totals.free, color: COLORS.gray }
  ] : [];

  return (
    <div className="space-y-6 animate-fade-in pb-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-black text-white">Dashboard</h1>
          <p className="text-[#666] text-sm mt-0.5">Centro de control GOATGYM</p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleRefresh}
          disabled={refreshing}
          className="text-[#666] hover:text-[#D4AF37]"
          data-testid="refresh-button"
        >
          <RefreshCw className={cn("w-5 h-5", refreshing && "animate-spin")} />
        </Button>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <StatsCard
          title="Total Usuarios"
          value={stats?.users?.total || 0}
          icon={Users}
          color="text-blue-400"
          bgColor="bg-blue-500/10"
          borderColor="border-blue-500/20"
          loading={loading}
          onClick={() => goToClients("all")}
          testId="stat-total-users"
        />
        <StatsCard
          title="Premium"
          value={stats?.users?.premium || 0}
          icon={Crown}
          color="text-[#D4AF37]"
          bgColor="bg-[#D4AF37]/10"
          borderColor="border-[#D4AF37]/20"
          loading={loading}
          onClick={() => goToClients("premium")}
          testId="stat-premium"
        />
        <StatsCard
          title="Free"
          value={stats?.users?.free || 0}
          icon={UserX}
          color="text-[#888]"
          bgColor="bg-[#888]/10"
          borderColor="border-[#888]/20"
          loading={loading}
          onClick={() => goToClients("free")}
          testId="stat-free"
        />
        <StatsCard
          title="Nuevos (Semana)"
          value={stats?.users?.new_this_week || 0}
          subtitle={`${stats?.users?.new_this_month || 0} este mes`}
          icon={TrendingUp}
          color="text-green-400"
          bgColor="bg-green-500/10"
          borderColor="border-green-500/20"
          loading={loading}
          testId="stat-new-users"
        />
      </div>

      {/* Charts Section */}
      {weeklyData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* New Users Chart */}
          <Card className="bg-[#121212] border-[#2A2A2A]">
            <CardHeader className="pb-2">
              <CardTitle className="text-white font-heading flex items-center gap-2 text-sm">
                <TrendingUp className="w-4 h-4 text-green-400" />
                Usuarios Nuevos por Semana
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[180px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={userChartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                    <XAxis 
                      dataKey="week" 
                      tick={{ fill: '#666', fontSize: 10 }} 
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fill: '#666', fontSize: 10 }} 
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="total" fill={COLORS.green} radius={[4, 4, 0, 0]} name="Usuarios" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Check-ins Chart */}
          <Card className="bg-[#121212] border-[#2A2A2A]">
            <CardHeader className="pb-2">
              <CardTitle className="text-white font-heading flex items-center gap-2 text-sm">
                <ClipboardCheck className="w-4 h-4 text-[#D4AF37]" />
                Check-ins por Semana
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[180px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={checkinChartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                    <XAxis 
                      dataKey="week" 
                      tick={{ fill: '#666', fontSize: 10 }} 
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fill: '#666', fontSize: 10 }} 
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line 
                      type="monotone" 
                      dataKey="total" 
                      stroke={COLORS.primary} 
                      strokeWidth={2}
                      dot={{ fill: COLORS.primary, strokeWidth: 0, r: 3 }}
                      name="Total"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="reviewed" 
                      stroke={COLORS.green} 
                      strokeWidth={2}
                      dot={{ fill: COLORS.green, strokeWidth: 0, r: 3 }}
                      name="Revisados"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Premium vs Free Pie Chart */}
          <Card className="bg-[#121212] border-[#2A2A2A] lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-white font-heading flex items-center gap-2 text-sm">
                <BarChart3 className="w-4 h-4 text-purple-400" />
                Distribución de Usuarios
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center gap-8">
                <div className="h-[120px] w-[120px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={35}
                        outerRadius={55}
                        dataKey="value"
                        stroke="none"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#D4AF37]" />
                    <span className="text-white text-sm font-medium">Premium</span>
                    <span className="text-[#D4AF37] font-bold">{weeklyData.totals.premium}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#666]" />
                    <span className="text-white text-sm font-medium">Free</span>
                    <span className="text-[#888] font-bold">{weeklyData.totals.free}</span>
                  </div>
                  <div className="pt-2 border-t border-[#2A2A2A]">
                    <span className="text-[#666] text-xs">
                      Ratio Premium: {((weeklyData.totals.premium / (weeklyData.totals.premium + weeklyData.totals.free)) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Check-ins Section */}
      <Card className="bg-[#121212] border-[#2A2A2A]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white font-heading flex items-center gap-2 text-lg">
              <ClipboardCheck className="w-5 h-5 text-[#D4AF37]" />
              Check-ins
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => goToClients("pending")}
              className="text-[#D4AF37] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 text-xs h-8"
            >
              Ver todos
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Check-in stats */}
          <div className="grid grid-cols-2 gap-3">
            <div 
              onClick={() => goToClients("pending")}
              className={cn(
                "p-3 rounded-lg bg-[#1A1A1A] border cursor-pointer transition-all hover:bg-[#222]",
                (stats?.checkins?.pending_review || 0) > 0 
                  ? "border-[#D4AF37]/50" 
                  : "border-[#2A2A2A]"
              )}
            >
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-orange-400" />
                <span className="text-xs text-[#888]">Pendientes</span>
              </div>
              <p className={cn(
                "text-2xl font-heading font-black mt-1",
                (stats?.checkins?.pending_review || 0) > 0 ? "text-[#D4AF37]" : "text-[#666]"
              )}>
                {loading ? "-" : stats?.checkins?.pending_review || 0}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A]">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="w-4 h-4 text-green-400" />
                <span className="text-xs text-[#888]">Revisados (sem)</span>
              </div>
              <p className="text-2xl font-heading font-black text-green-400 mt-1">
                {loading ? "-" : stats?.checkins?.reviewed_this_week || 0}
              </p>
            </div>
          </div>

          {/* Pending clients list */}
          {pendingClients.length > 0 ? (
            <div className="space-y-2 pt-2">
              <p className="text-xs text-[#666] font-medium uppercase tracking-wider">
                Requieren revisión
              </p>
              {pendingClients.map((client) => (
                <ClientCard
                  key={client.id}
                  client={client}
                  onClick={goToPremiumClient}
                  compact
                />
              ))}
            </div>
          ) : !loading && (
            <div className="text-center py-4">
              <ClipboardCheck className="w-8 h-8 text-green-500/30 mx-auto mb-2" />
              <p className="text-sm text-[#666]">Todo al día</p>
              <p className="text-xs text-[#555]">No hay check-ins pendientes</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="bg-[#121212] border-[#2A2A2A]">
        <CardHeader className="pb-3">
          <CardTitle className="text-white font-heading flex items-center gap-2 text-lg">
            <Activity className="w-5 h-5 text-[#D4AF37]" />
            Acciones Rápidas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <QuickAction
              href="/admin/clients"
              icon={Users}
              label="Clientes"
              color="text-blue-400"
              bgColor="bg-blue-500/10"
              borderColor="border-blue-500/30"
              testId="quick-clients"
            />
            <QuickAction
              href="/admin/premium-clients"
              icon={Crown}
              label="Premium"
              color="text-[#D4AF37]"
              bgColor="bg-[#D4AF37]/10"
              borderColor="border-[#D4AF37]/30"
              testId="quick-premium"
            />
            <QuickAction
              href="/admin/exercises"
              icon={Dumbbell}
              label="Ejercicios"
              color="text-purple-400"
              bgColor="bg-purple-500/10"
              borderColor="border-purple-500/30"
              testId="quick-exercises"
            />
            <QuickAction
              href="/admin/recipes"
              icon={ChefHat}
              label="Recetas"
              color="text-orange-400"
              bgColor="bg-orange-500/10"
              borderColor="border-orange-500/30"
              testId="quick-recipes"
            />
            <QuickAction
              href="/admin/foods"
              icon={Apple}
              label="Alimentos"
              color="text-green-400"
              bgColor="bg-green-500/10"
              borderColor="border-green-500/30"
              testId="quick-foods"
            />
            <QuickAction
              href="/admin/programs"
              icon={Calendar}
              label="Programas"
              color="text-cyan-400"
              bgColor="bg-cyan-500/10"
              borderColor="border-cyan-500/30"
              testId="quick-programs"
            />
            <QuickAction
              href="/admin/custom-programs"
              icon={Dumbbell}
              label="Entrenos Personal."
              color="text-pink-400"
              bgColor="bg-pink-500/10"
              borderColor="border-pink-500/30"
              testId="quick-custom-programs"
            />
            <QuickAction
              href="/admin/goat-library"
              icon={Library}
              label="Biblioteca GOAT"
              color="text-amber-400"
              bgColor="bg-amber-500/10"
              borderColor="border-amber-500/30"
              testId="quick-goat-library"
            />
          </div>
        </CardContent>
      </Card>

      {/* Info Banner */}
      {stats?.checkins?.pending_review > 0 && (
        <div 
          onClick={() => goToClients("pending")}
          className="p-4 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 cursor-pointer hover:bg-[#D4AF37]/15 transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div className="flex-1">
              <p className="text-white font-semibold text-sm">
                {stats.checkins.pending_review} check-in{stats.checkins.pending_review !== 1 ? "s" : ""} pendiente{stats.checkins.pending_review !== 1 ? "s" : ""}
              </p>
              <p className="text-[#D4AF37]/80 text-xs">
                Toca para revisar
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-[#D4AF37]" />
          </div>
        </div>
      )}
    </div>
  );
};

// Quick Action Button Component
const QuickAction = ({ href, icon: Icon, label, color, bgColor, borderColor, testId }) => (
  <a
    href={href}
    className={cn(
      "p-4 rounded-xl bg-[#1A1A1A] border transition-all text-center",
      borderColor,
      "hover:bg-[#222] active:scale-[0.98]"
    )}
    data-testid={testId}
  >
    <div className={cn("w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center", bgColor)}>
      <Icon className={cn("w-5 h-5", color)} />
    </div>
    <p className="text-white text-sm font-medium">{label}</p>
  </a>
);

export default AdminDashboard;
